import { useEffect, useRef } from "react";
import * as THREE from "three";
import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry.js";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment.js";
import { useMotionValueEvent } from "framer-motion";
import { smoothstep, lerp, easeInOut } from "./timeline";

/**
 * LaptopCanvas - vanilla three.js premium laptop, scroll-choreographed.
 *
 * Desktop timeline (progress p = 0..1 across the pinned section):
 *  0.00–0.16  laptop glides from hero right side to centre
 *  0.10–0.26  lid opens
 *  0.26–0.40  rotates to face camera, camera dollies in
 *  0.40–0.52  camera zooms until the screen fills the viewport
 *  0.52–0.90  (DOM "Walsh OS" overlay covers the viewport)
 *  0.90–0.96  camera pulls back out
 *  0.945–1.0  lid closes, laptop settles centre-frame
 *
 * Mobile mode: short pinned scene - centre, open, face camera.
 */

const W = 3.3; // chassis width
const D = 2.2; // chassis depth
const BASE_H = 0.11;
const LID_T = 0.06;
const HINGE_Z = -D / 2 + 0.05;
const LID_LEN = D - 0.08;
const LID_OPEN_FINAL = -Math.PI / 2; // screen exactly vertical, facing +z

function drawWallpaper(canvas) {
  const ctx = canvas.getContext("2d");
  const w = canvas.width;
  const h = canvas.height;

  ctx.fillStyle = "#0b0d0f";
  ctx.fillRect(0, 0, w, h);

  const glow = (x, y, r, c0) => {
    const g = ctx.createRadialGradient(x, y, 0, x, y, r);
    g.addColorStop(0, c0);
    g.addColorStop(1, "rgba(11,13,15,0)");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);
  };
  glow(w * 0.18, h * 0.2, w * 0.5, "rgba(13,148,136,0.30)");
  glow(w * 0.85, h * 0.85, w * 0.55, "rgba(16,185,129,0.20)");
  glow(w * 0.6, h * 0.05, w * 0.3, "rgba(45,212,191,0.12)");

  // fine grid
  ctx.strokeStyle = "rgba(255,255,255,0.035)";
  ctx.lineWidth = 1;
  const step = 56;
  ctx.beginPath();
  for (let x = step; x < w; x += step) {
    ctx.moveTo(x, 0);
    ctx.lineTo(x, h);
  }
  for (let y = step; y < h; y += step) {
    ctx.moveTo(0, y);
    ctx.lineTo(w, y);
  }
  ctx.stroke();

  // top bar
  ctx.fillStyle = "rgba(255,255,255,0.05)";
  ctx.fillRect(0, 0, w, 34);
  ctx.fillStyle = "rgba(255,255,255,0.85)";
  ctx.font = "600 17px Outfit, sans-serif";
  ctx.fillText("Website Walsh", 26, 23);
  ctx.textAlign = "right";
  ctx.fillStyle = "rgba(255,255,255,0.5)";
  ctx.font = "500 15px Manrope, sans-serif";
  ctx.fillText("Walsh OS", w - 26, 23);
  ctx.textAlign = "left";
}

function makeShadowTexture() {
  const c = document.createElement("canvas");
  c.width = c.height = 256;
  const ctx = c.getContext("2d");
  const g = ctx.createRadialGradient(128, 128, 10, 128, 128, 128);
  g.addColorStop(0, "rgba(0,0,0,0.42)");
  g.addColorStop(0.6, "rgba(0,0,0,0.18)");
  g.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 256, 256);
  const t = new THREE.CanvasTexture(c);
  return t;
}

export default function LaptopCanvas({ progress, mode = "desktop" }) {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const progressRef = useRef(0);

  useMotionValueEvent(progress, "change", (v) => {
    progressRef.current = v;
  });

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;

    const renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      alpha: true,
      powerPreference: "high-performance",
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(wrap.clientWidth, wrap.clientHeight, false);
    renderer.setClearColor(0x000000, 0);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.12;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      34,
      wrap.clientWidth / wrap.clientHeight,
      0.1,
      60,
    );

    // Studio reflections for the metal
    const pmrem = new THREE.PMREMGenerator(renderer);
    const envTex = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
    scene.environment = envTex;

    scene.add(new THREE.HemisphereLight(0xffffff, 0xd9d7d0, 0.5));
    const key = new THREE.DirectionalLight(0xffffff, 1.35);
    key.position.set(4, 6, 5);
    scene.add(key);
    const rim = new THREE.DirectionalLight(0x2dd4bf, 0.5);
    rim.position.set(-5, 3, -4);
    scene.add(rim);

    // ---------- Materials ----------
    const aluminium = new THREE.MeshStandardMaterial({
      color: 0xd6d8db,
      metalness: 0.9,
      roughness: 0.32,
      envMapIntensity: 1.15,
    });
    const aluminiumDark = new THREE.MeshStandardMaterial({
      color: 0xb4b7bb,
      metalness: 0.92,
      roughness: 0.36,
      envMapIntensity: 1.0,
    });
    const deckMat = new THREE.MeshStandardMaterial({
      color: 0x14161a,
      metalness: 0.4,
      roughness: 0.7,
    });
    const keyMat = new THREE.MeshStandardMaterial({
      color: 0x232529,
      metalness: 0.3,
      roughness: 0.62,
    });
    const bezelMat = new THREE.MeshStandardMaterial({
      color: 0x0a0a0c,
      metalness: 0.35,
      roughness: 0.5,
    });

    // ---------- Laptop ----------
    const laptop = new THREE.Group();
    scene.add(laptop);

    // Chassis
    const base = new THREE.Mesh(
      new RoundedBoxGeometry(W, BASE_H, D, 4, 0.035),
      aluminium,
    );
    laptop.add(base);

    // Keyboard deck inset
    const deck = new THREE.Mesh(
      new RoundedBoxGeometry(W - 0.42, 0.018, 1.34, 3, 0.02),
      deckMat,
    );
    deck.position.set(0, BASE_H / 2 + 0.004, -0.24);
    laptop.add(deck);

    // Keys - rows of unit-width recipes (total 15 units per row)
    const UNIT = (W - 0.62) / 15; // key pitch unit
    const KEY_GAP = 0.028;
    const ROWS = [
      [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2],
      [1.5, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1.5],
      [1.75, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2.25],
      [2.25, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2.75],
      [1.25, 1.25, 1.25, 6.5, 1.25, 1.25, 1.25, 1.25],
    ];
    const keyGeoCache = new Map();
    const keyRowH = 0.21;
    ROWS.forEach((row, r) => {
      let cursor = -(15 * UNIT) / 2;
      row.forEach((u) => {
        const kw = u * UNIT - KEY_GAP;
        const kh = keyRowH - KEY_GAP;
        const id = `${u.toFixed(2)}`;
        if (!keyGeoCache.has(id)) {
          keyGeoCache.set(
            id,
            new RoundedBoxGeometry(kw, 0.028, kh, 2, 0.012),
          );
        }
        const key = new THREE.Mesh(keyGeoCache.get(id), keyMat);
        key.position.set(
          cursor + (u * UNIT) / 2,
          BASE_H / 2 + 0.02,
          -0.82 + r * 0.24,
        );
        laptop.add(key);
        cursor += u * UNIT;
      });
    });

    // Trackpad
    const trackpad = new THREE.Mesh(
      new RoundedBoxGeometry(1.16, 0.014, 0.74, 3, 0.03),
      aluminiumDark,
    );
    trackpad.position.set(0, BASE_H / 2 + 0.004, 0.6);
    laptop.add(trackpad);

    // Front lip notch
    const lip = new THREE.Mesh(
      new RoundedBoxGeometry(0.5, 0.02, 0.05, 2, 0.01),
      aluminiumDark,
    );
    lip.position.set(0, -0.01, D / 2 - 0.008);
    laptop.add(lip);

    // Hinge barrel
    const hinge = new THREE.Mesh(
      new THREE.CylinderGeometry(0.042, 0.042, W - 1.0, 20),
      new THREE.MeshStandardMaterial({
        color: 0x8f9297,
        metalness: 0.95,
        roughness: 0.3,
      }),
    );
    hinge.rotation.z = Math.PI / 2;
    hinge.position.set(0, BASE_H / 2 + 0.005, HINGE_Z);
    laptop.add(hinge);

    // Lid pivot at hinge line
    const lidPivot = new THREE.Group();
    lidPivot.position.set(0, BASE_H / 2 + 0.006, HINGE_Z);
    laptop.add(lidPivot);

    // Lid shell - extends +z from pivot so it lies flat over the base when closed
    const lid = new THREE.Mesh(
      new RoundedBoxGeometry(W, LID_T, LID_LEN, 4, 0.028),
      aluminium,
    );
    lid.position.set(0, 0, LID_LEN / 2 - 0.02);
    lidPivot.add(lid);

    // Bezel on the inside face
    const bezel = new THREE.Mesh(
      new RoundedBoxGeometry(W - 0.16, 0.01, LID_LEN - 0.16, 3, 0.008),
      bezelMat,
    );
    bezel.position.set(0, -LID_T / 2 - 0.003, LID_LEN / 2 - 0.02);
    lidPivot.add(bezel);

    // Screen - canvas texture with the Walsh OS wallpaper
    const screenCanvas = document.createElement("canvas");
    screenCanvas.width = 1280;
    screenCanvas.height = 800;
    drawWallpaper(screenCanvas);
    const screenTex = new THREE.CanvasTexture(screenCanvas);
    screenTex.colorSpace = THREE.SRGBColorSpace;
    screenTex.anisotropy = 4;
    // Redraw once brand fonts arrive so the on-screen wordmark uses Outfit
    document.fonts?.ready?.then(() => {
      drawWallpaper(screenCanvas);
      screenTex.needsUpdate = true;
    });
    const screen = new THREE.Mesh(
      new THREE.PlaneGeometry(W - 0.32, LID_LEN - 0.32),
      new THREE.MeshBasicMaterial({ map: screenTex, toneMapped: false }),
    );
    screen.rotation.x = Math.PI / 2; // normal faces -y (toward keyboard when closed)
    screen.position.set(0, -LID_T / 2 - 0.009, LID_LEN / 2 - 0.02);
    lidPivot.add(screen);

    // Fake soft contact shadow
    const shadow = new THREE.Mesh(
      new THREE.PlaneGeometry(W * 1.3, D * 1.4),
      new THREE.MeshBasicMaterial({
        map: makeShadowTexture(),
        transparent: true,
        depthWrite: false,
        opacity: 0.5,
      }),
    );
    shadow.rotation.x = -Math.PI / 2;
    shadow.renderOrder = -1;
    scene.add(shadow);

    // ---------- Resize ----------
    const onResize = () => {
      renderer.setSize(wrap.clientWidth, wrap.clientHeight, false);
      camera.aspect = wrap.clientWidth / wrap.clientHeight;
      camera.updateProjectionMatrix();
    };
    window.addEventListener("resize", onResize);

    // ---------- Pause when off-screen ----------
    let inView = true;
    const io = new IntersectionObserver(
      ([entry]) => {
        inView = entry.isIntersecting;
      },
      { threshold: 0 },
    );
    io.observe(wrap);

    // ---------- Choreography ----------
    const isMobile = mode === "mobile";
    let raf;
    let time = 0;

    const render = () => {
      raf = requestAnimationFrame(render);
      if (!inView) return;
      time += 0.016;
      const p = progressRef.current;
      const aspect = camera.aspect;

      if (!isMobile) {
        // ===== DESKTOP TIMELINE =====
        // Segment 1 - hero → centre
        const s1 = easeInOut(smoothstep(p, 0.02, 0.15));
        // Segment 2 - lid opens
        const s2 = easeInOut(smoothstep(p, 0.1, 0.26));
        // Segment 3 - rotate to face camera
        const s3 = easeInOut(smoothstep(p, 0.26, 0.4));
        // Segment 4 - zoom into screen
        const s4 = easeInOut(smoothstep(p, 0.4, 0.52));
        // Segment 5 - pull back out (starts later: overview pause holds 0.83–0.93)
        const s5 = easeInOut(smoothstep(p, 0.935, 0.978));
        // Segment 6 - close + settle
        const s6 = easeInOut(smoothstep(p, 0.968, 1.0));

        // Laptop pose
        // Vertical drop has its own segment: starts as the lid gets tall (0.20)
        // and settles well before the zoom (0.40) so the open laptop always
        // sits comfortably below the fixed header.
        const sy = easeInOut(smoothstep(p, 0.12, 0.32));
        const posX = lerp(1.62, 0, s1);
        let posY = lerp(-0.34, -0.36, s1);
        posY = lerp(posY, -1.3, sy);
        posY -= 0.28 * s3; // compensate the dolly-in so the top bezel stays clear of the header until the zoom
        posY = lerp(posY, -0.75, s5);
        posY = lerp(posY, -0.42, s6);
        let posZ = lerp(0, 0.15, s3);
        posZ = lerp(posZ, 0, s5);

        let rotY = lerp(-0.46, -0.08, s1);
        rotY = lerp(rotY, 0, s3);
        rotY = lerp(rotY, 0.44, s5);
        rotY = lerp(rotY, 0.3, s6);
        let rotX = lerp(0.1, 0.04, s1);
        rotX = lerp(rotX, 0, s3);
        rotX = lerp(rotX, 0.06, s5);

        let scale = lerp(0.72, 1.0, s1);
        scale = lerp(scale, 1.04, s3);
        scale = lerp(scale, 0.92, s5);

        let lidRot = lerp(0, -1.78, s2);
        lidRot = lerp(lidRot, LID_OPEN_FINAL, s3);
        lidRot = lerp(lidRot, -1.9, s5);
        lidRot = lerp(lidRot, 0, s6);
        lidPivot.rotation.x = lidRot;

        // gentle idle float, frozen while zoomed / exiting
        const floatAmp = 0.02 * (1 - s4) * (1 - s5);
        laptop.position.set(
          posX,
          posY + Math.sin(time * 0.7) * floatAmp,
          posZ,
        );
        laptop.rotation.set(rotX, rotY, 0);
        laptop.scale.setScalar(scale);

        // Screen world position once the lid is vertical (used for the zoom)
        const pivotY = posY + BASE_H / 2 + 0.006;
        const pivotZ = posZ + HINGE_Z;
        const screenC = LID_LEN / 2 - 0.02; // local z of screen centre
        const screenY = pivotY + screenC; // lid vertical: local +z → +y
        const screenZ = pivotZ + LID_T / 2 + 0.01;

        // Camera
        let camX = 0;
        let camY = lerp(0.44, 0.4, s1);
        let camZ = lerp(6.1, 5.5, s1);
        camY = lerp(camY, 0.34, s3);
        camZ = lerp(camZ, 4.35, s3);
        // zoom to fill: distance so screen width covers the frustum width
        const dFill =
          ((W - 0.32) / 2 / Math.tan((camera.fov * Math.PI) / 360) / aspect) *
          0.985;
        camX = lerp(camX, 0, s4);
        camY = lerp(camY, screenY, s4);
        camZ = lerp(camZ, screenZ + dFill, s4);
        // pull back out
        camY = lerp(camY, 0.85, s5);
        camZ = lerp(camZ, 6.6, s5);
        camY = lerp(camY, 0.5, s6);
        camZ = lerp(camZ, 6.0, s6);

        let lookX = lerp(0.55, 0, s1);
        let lookY = lerp(-0.12, 0.02, s1);
        let lookZ = 0;
        lookY = lerp(lookY, 0.12, s3);
        lookZ = lerp(lookZ, -0.5, s3);
        lookX = lerp(lookX, 0, s4);
        lookY = lerp(lookY, screenY, s4);
        lookZ = lerp(lookZ, screenZ, s4);
        lookY = lerp(lookY, 0.05, s5);
        lookZ = lerp(lookZ, 0, s5);
        lookY = lerp(lookY, -0.1, s6);

        camera.position.set(camX, camY, camZ);
        camera.lookAt(lookX, lookY, lookZ);

        // Shadow follows
        shadow.position.set(posX, posY - BASE_H / 2 - 0.045, posZ + 0.1);
        shadow.scale.setScalar(scale * (1 - s4));
        shadow.material.opacity =
          0.8 * (1 - s4) * (mode === "desktop" ? 1 : 1);
      } else {
        // ===== MOBILE: centre → open → face camera =====
        const s1 = easeInOut(smoothstep(p, 0.0, 0.3));
        const s2 = easeInOut(smoothstep(p, 0.3, 0.7));
        const s3 = easeInOut(smoothstep(p, 0.7, 1.0));

        const posY = lerp(-0.3, -0.85, s3);
        let rotY = lerp(-0.4, 0, s1);
        rotY = lerp(rotY, 0, s3);
        const scale = lerp(0.8, 1.0, s1);
        let lidRot = lerp(0, -1.7, s2);
        lidRot = lerp(lidRot, LID_OPEN_FINAL, s3);
        lidPivot.rotation.x = lidRot;

        laptop.position.set(0, posY + Math.sin(time * 0.7) * 0.015, 0);
        laptop.rotation.set(0.05, rotY, 0);
        laptop.scale.setScalar(scale);

        camera.position.set(0, lerp(0.4, 0.2, s3), lerp(9.6, 7.4, s3));
        camera.lookAt(0, lerp(-0.1, 0.05, s3), -0.4);

        shadow.position.set(0, posY - BASE_H / 2 - 0.045, 0.1);
        shadow.scale.setScalar(scale);
      }

      renderer.render(scene, camera);
    };
    render();

    return () => {
      cancelAnimationFrame(raf);
      io.disconnect();
      window.removeEventListener("resize", onResize);
      pmrem.dispose();
      envTex.dispose();
      renderer.dispose();
      screenTex.dispose();
      scene.traverse((o) => {
        if (o.isMesh) {
          o.geometry?.dispose?.();
        }
      });
      keyGeoCache.forEach((g) => g.dispose());
      [aluminium, aluminiumDark, deckMat, keyMat, bezelMat].forEach((m) =>
        m.dispose(),
      );
    };
  }, [mode]);

  return (
    <div ref={wrapRef} className="absolute inset-0" data-testid="laptop-canvas-wrap">
      <canvas ref={canvasRef} data-testid="laptop-canvas" className="w-full h-full" />
    </div>
  );
}
